'use client';

import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import {
  ArrowPathIcon,
  ArrowsPointingInIcon,
  CubeIcon,
  CubeTransparentIcon,
} from '@heroicons/react/20/solid';
import Image from 'next/image';
import ReactCrop, { Crop, PercentCrop, PixelCrop } from 'react-image-crop';

import { toBlob } from '@northladder/utilities';

import {
  ButtonBack,
  ButtonPreviewImage,
  ButtonUploadImage,
} from './components';
import { useDebounceEffect } from './useDebounceEffect';
import { centerAspectCrop, canvasPreview, CROP_ASPECT_RATIO } from './utils';

export interface IReactImageCropperProps {
  imgSrc: string;
  onPreviewUpdate: (imgURL: string) => void;
  onCancelCrop: () => void;
  onImageUpload: (imgURL: string) => void;
}

export function OverlayReactImageCropper({
  imgSrc,
  onCancelCrop,
  onPreviewUpdate,
  onImageUpload,
}: IReactImageCropperProps) {
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);

  const [aspect, setAspect] = useState<number | undefined>(CROP_ASPECT_RATIO);

  const [previewFileURL, setPreviewFileURL] = useState('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  /**
   * Generates an image URL from a the canvas preview
   */
  const getUpdatedImgURL = async () => {
    if (previewCanvasRef.current) {
      const imgURL = await toBlob(previewCanvasRef.current, previewFileURL);

      if (imgURL) {
        setPreviewFileURL(imgURL);
        onPreviewUpdate(imgURL);

        return imgURL;
      }
    }

    return previewFileURL;
  };

  useEffect(() => {
    const initializeImg = async () => {
      getUpdatedImgURL();
    };

    initializeImg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgSrc, previewCanvasRef.current]);

  const onImageLoad = async (e: React.SyntheticEvent<HTMLImageElement>) => {
    await getUpdatedImgURL();

    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspect || 1));
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate
        );
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  function handleToggleAspectClick() {
    if (aspect) {
      setAspect(undefined);
    } else if (imgRef.current) {
      const { width, height } = imgRef.current;
      setAspect(9 / 16);
      setCrop(centerAspectCrop(width, height, CROP_ASPECT_RATIO));
    }
  }

  const handleOnCropChange = (_: PixelCrop, percentCrop: PercentCrop) => {
    setCrop(percentCrop);
  };

  const handleOnCropComplete = async (newCrop: PixelCrop) => {
    await getUpdatedImgURL();
    setCompletedCrop(newCrop);
  };

  const onChangeRotation = async (event: ChangeEvent<HTMLInputElement>) => {
    setRotate(Math.min(180, Math.max(-180, Number(event.target.value))));
    await getUpdatedImgURL();
  };

  const handleOnClickPreview = async () => {
    await getUpdatedImgURL();
    setIsPreviewMode(true);
  };

  const handleOnClickCancelPreview = async () => {
    setIsPreviewMode(() => {
      setPreviewFileURL(previewFileURL);
      return false;
    });
  };

  const handleOnClickCancelCrop = async () => {
    onCancelCrop();
    setIsPreviewMode(false);
  };

  const handleOnClickUpload = async () => {
    const imgURL = await getUpdatedImgURL();
    onImageUpload(imgURL);
  };

  return (
    <div className="crop-container">
      {imgSrc ? (
        <ReactCrop
          aspect={aspect}
          className="h-full max-h-full"
          crop={crop}
          onChange={handleOnCropChange}
          onComplete={handleOnCropComplete}
        >
          <Image
            ref={imgRef}
            alt="Crop me"
            className="h-full w-full object-contain"
            height={crop?.height || 780}
            onLoad={onImageLoad}
            src={imgSrc}
            style={{
              transform: `scale(${scale}) rotate(${rotate}deg)`,
              opacity: 0.25,
              pointerEvents: 'none',
            }}
            width={crop?.width || 360}
          />
        </ReactCrop>
      ) : null}

      <div
        className="
          absolute top-0 flex hidden w-full justify-between gap-8 bg-gray-700/50
          p-1 px-2 opacity-60
        "
      >
        <div className="flex h-8 items-center">
          <label className="p-2 text-sm text-gray-600" htmlFor="scale-input">
            <ArrowsPointingInIcon className="h-5 w-5 fill-gray-50" />
          </label>
          <input
            className="h-8 w-16"
            disabled={!imgSrc}
            id="scale-input"
            onChange={(e) => setScale(Number(e.target.value))}
            step="0.1"
            type="number"
            value={scale}
          />
        </div>
        <div className="flex h-8 items-center">
          <label className="p-2 text-sm text-gray-600" htmlFor="rotate-input">
            <ArrowPathIcon className="h-5 w-5 fill-gray-50" />
          </label>
          <input
            className="h-8 w-16"
            disabled={!imgSrc}
            id="rotate-input"
            onChange={onChangeRotation}
            type="number"
            value={rotate}
          />
        </div>
        <div className="z-[80]">
          <button
            className="p-2"
            onClick={handleToggleAspectClick}
            type="button"
          >
            {aspect ? (
              <CubeTransparentIcon className="h-5 w-5 fill-gray-50" />
            ) : (
              <CubeIcon className="h-5 w-5 fill-gray-50" />
            )}
          </button>
        </div>
      </div>

      <div
        className="
          absolute bottom-0 left-0 z-10 mt-5 flex hidden w-full
          justify-between gap-1 p-2 opacity-80 sm:mt-6
        "
      >
        {isPreviewMode ? (
          <>
            <ButtonBack onClick={handleOnClickCancelPreview} />
            <ButtonUploadImage onClick={handleOnClickUpload} />
          </>
        ) : (
          <>
            <ButtonBack onClick={handleOnClickCancelCrop} />
            <ButtonPreviewImage onClick={handleOnClickPreview} />
          </>
        )}
      </div>

      <div className="pointer-events-none absolute top-0 hidden opacity-50">
        {completedCrop ? (
          <canvas
            ref={previewCanvasRef}
            style={{
              border: '0.5px solid black',
              objectFit: 'contain',
              width: completedCrop.width,
              height: completedCrop.height,
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
